var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/fetcher.ts
import { createTraceparent } from "@repo/telemetry";
var TWO_FACTOR_STEP_UP_REQUIRED = "TWO_FACTOR_STEP_UP_REQUIRED";
async function readStepUpChallenge(res) {
  try {
    const source = typeof res.clone === "function" ? res.clone() : res;
    const body = await source.json();
    if (body?.code !== TWO_FACTOR_STEP_UP_REQUIRED) return null;
    const message = typeof body.message === "string" && body.message ? body.message : "2FA verification required";
    return {
      message
    };
  } catch {
    return null;
  }
}
__name(readStepUpChallenge, "readStepUpChallenge");
var ApiError = class extends Error {
  static {
    __name(this, "ApiError");
  }
  status;
  code;
  details;
  requestId;
  constructor(status, code, message, details, requestId) {
    super(message), this.status = status, this.code = code, this.details = details, this.requestId = requestId;
    this.name = "ApiError";
  }
};
function normalizeVersionedPath(path) {
  return path.replace(/^\/(staff|agent|customer)\/v(\d+)\//, "/v$2/$1/");
}
__name(normalizeVersionedPath, "normalizeVersionedPath");
function parseRetryAfterMs(res) {
  for (const [key, value] of res.headers.entries()) {
    if (key.toLowerCase().startsWith("retry-after")) {
      const seconds = Number(value);
      if (Number.isFinite(seconds)) return seconds * 1e3;
    }
  }
  return void 0;
}
__name(parseRetryAfterMs, "parseRetryAfterMs");
function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : rand & 3 | 8;
    return value.toString(16);
  });
}
__name(generateIdempotencyKey, "generateIdempotencyKey");
function createFetcher(config) {
  let refreshing = null;
  async function attemptRefresh() {
    const refreshToken = config.getRefreshToken?.();
    if (!refreshToken) return null;
    try {
      const res = await fetch(`${config.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${refreshToken}`
        }
      });
      if (!res.ok) return null;
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      const newAccess = res.headers.get("set-auth-token") || data.accessToken;
      const newRefresh = data.sessionToken || data.refreshToken || refreshToken;
      if (!newAccess) return null;
      config.onTokensRefreshed?.(newAccess, newRefresh);
      return newAccess;
    } catch (error) {
      console.warn("[api-client] token refresh failed", error);
      config.onError?.(error, {
        component: "api-client",
        action: "attemptRefresh"
      });
      return null;
    }
  }
  __name(attemptRefresh, "attemptRefresh");
  async function request(method, path, body, options, isRetry = false, idempotencyKey) {
    const token = config.getToken?.();
    const orgUnitId = config.getOrgUnitId?.();
    const clientContext = config.getClientContext?.();
    const headers = {};
    if (body !== void 0) headers["Content-Type"] = "application/json";
    if (token && !(config.cookieFirst && !isRetry)) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (orgUnitId) headers["x-org-unit-id"] = orgUnitId;
    if (clientContext) headers["x-client-context"] = clientContext;
    const requestId = options?.headers?.["x-request-id"] ?? (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : generateIdempotencyKey());
    headers["x-request-id"] = requestId;
    if (!headers["traceparent"]) {
      headers["traceparent"] = createTraceparent();
    }
    if (method === "POST" || method === "PUT" || method === "PATCH") {
      idempotencyKey = idempotencyKey ?? generateIdempotencyKey();
      headers["Idempotency-Key"] = idempotencyKey;
    }
    if (options?.headers) Object.assign(headers, options.headers);
    let res;
    try {
      res = await fetch(`${config.baseUrl}${normalizeVersionedPath(path)}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : void 0,
        signal: options?.signal
      });
    } catch (networkError) {
      config.onError?.(networkError, {
        component: "api-client",
        action: "networkFailure",
        method,
        path,
        requestId
      });
      throw networkError;
    }
    options?.onResponse?.(res);
    if (res.status === 401 && !options?.skipAuthRedirect) {
      const stepUp = await readStepUpChallenge(res);
      if (stepUp) {
        if (!isRetry && config.onTwoFactorStepUp && await config.onTwoFactorStepUp()) {
          return request(method, path, body, options, true, idempotencyKey);
        }
        throw new ApiError(401, TWO_FACTOR_STEP_UP_REQUIRED, stepUp.message, void 0, requestId);
      }
    }
    if (res.status === 401 && token != null && !options?.skipAuthRedirect) {
      if (!isRetry) {
        if (config.cookieFirst) {
          return request(method, path, body, options, true, idempotencyKey);
        }
        if (!refreshing) {
          refreshing = attemptRefresh().finally(() => {
            refreshing = null;
          });
        }
        const newToken = await refreshing;
        if (newToken) {
          return request(method, path, body, options, true, idempotencyKey);
        }
        config.onLogout?.();
        throw new ApiError(401, "UNAUTHORIZED", "Session expired", void 0, requestId);
      }
      if (config.cookieFirst) {
        if (!refreshing) {
          refreshing = attemptRefresh().finally(() => {
            refreshing = null;
          });
        }
        const newToken = await refreshing;
        if (newToken) {
          return request(method, path, body, options, true, idempotencyKey);
        }
      }
      config.onLogout?.();
      throw new ApiError(401, "UNAUTHORIZED", "Session expired", void 0, requestId);
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const respRequestId = res.headers.get("x-request-id") ?? json.requestId ?? requestId;
      const bodyDetails = json.details ?? json?.error?.details;
      const details = res.status === 429 ? {
        ...bodyDetails,
        retryAfterMs: parseRetryAfterMs(res)
      } : bodyDetails;
      const apiErr = new ApiError(res.status, json.code ?? json?.error?.code ?? "UNKNOWN_ERROR", json.message ?? json?.error?.message ?? "Request failed", details, respRequestId);
      config.onError?.(apiErr, {
        component: "api-client",
        method,
        path,
        status: res.status,
        requestId: respRequestId
      });
      throw apiErr;
    }
    const hasData = json.data !== void 0;
    const hasMeta = json.meta !== void 0;
    if (hasData && !hasMeta) {
      return json.data;
    }
    return json;
  }
  __name(request, "request");
  return {
    get: /* @__PURE__ */ __name((path, opts) => request("GET", path, void 0, opts), "get"),
    post: /* @__PURE__ */ __name((path, body, opts) => request("POST", path, body, opts), "post"),
    patch: /* @__PURE__ */ __name((path, body, opts) => request("PATCH", path, body, opts), "patch"),
    put: /* @__PURE__ */ __name((path, body, opts) => request("PUT", path, body, opts), "put"),
    delete: /* @__PURE__ */ __name((path, opts) => request("DELETE", path, void 0, opts), "delete")
  };
}
__name(createFetcher, "createFetcher");

// src/create-resource-client.ts
function createResourceClient(fetcherConfig, opts) {
  const fetcher = createFetcher(fetcherConfig);
  return {
    list: /* @__PURE__ */ __name(async (params) => {
      const query = params ? "?" + new URLSearchParams(params).toString() : "";
      const response = await fetcher.get(`${opts.path}${query}`);
      const rawItems = Array.isArray(response) ? response : response && typeof response === "object" && "data" in response && Array.isArray(response.data) ? response.data : [];
      const meta = response && typeof response === "object" && "meta" in response ? response.meta : {
        total: rawItems.length,
        page: 1,
        limit: rawItems.length,
        totalPages: 1
      };
      const parsedItems = [];
      for (const item of rawItems) {
        const parsed = opts.schemas.listItem.safeParse(item);
        if (parsed.success) {
          parsedItems.push(parsed.data);
        } else {
          parsedItems.push(item);
        }
      }
      return {
        data: parsedItems,
        meta
      };
    }, "list"),
    get: /* @__PURE__ */ __name(async (id) => {
      const response = await fetcher.get(`${opts.path}/${id}`);
      const rawDetail = response && typeof response === "object" && "data" in response ? response.data : response;
      const parsed = opts.schemas.detail.safeParse(rawDetail);
      return parsed.success ? parsed.data : rawDetail;
    }, "get"),
    create: /* @__PURE__ */ __name(async (body) => {
      const response = await fetcher.post(opts.path, body);
      const rawDetail = response && typeof response === "object" && "data" in response ? response.data : response;
      const parsed = opts.schemas.detail.safeParse(rawDetail);
      return parsed.success ? parsed.data : rawDetail;
    }, "create"),
    update: /* @__PURE__ */ __name(async (id, body) => {
      const response = await fetcher.patch(`${opts.path}/${id}`, body);
      const rawDetail = response && typeof response === "object" && "data" in response ? response.data : response;
      const parsed = opts.schemas.detail.safeParse(rawDetail);
      return parsed.success ? parsed.data : rawDetail;
    }, "update"),
    remove: /* @__PURE__ */ __name((id) => fetcher.delete(`${opts.path}/${id}`), "remove")
  };
}
__name(createResourceClient, "createResourceClient");

// src/create-resource-hooks.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
function createResourceHooks(client, queryKeyPrefix) {
  return {
    useList: /* @__PURE__ */ __name((params, options) => useQuery({
      ...options,
      queryKey: [
        ...queryKeyPrefix,
        "list",
        params
      ],
      queryFn: /* @__PURE__ */ __name(() => client.list(params), "queryFn")
    }), "useList"),
    useGet: /* @__PURE__ */ __name((id, options) => useQuery({
      ...options,
      queryKey: [
        ...queryKeyPrefix,
        "detail",
        id
      ],
      queryFn: /* @__PURE__ */ __name(() => client.get(id), "queryFn"),
      enabled: !!id
    }), "useGet"),
    useCreate: /* @__PURE__ */ __name(() => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: /* @__PURE__ */ __name((body) => client.create(body), "mutationFn"),
        onSuccess: /* @__PURE__ */ __name(() => qc.invalidateQueries({
          queryKey: [
            ...queryKeyPrefix,
            "list"
          ]
        }), "onSuccess")
      });
    }, "useCreate"),
    useUpdate: /* @__PURE__ */ __name(() => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: /* @__PURE__ */ __name(({ id, body }) => client.update(id, body), "mutationFn"),
        onSuccess: /* @__PURE__ */ __name((_, { id }) => {
          qc.invalidateQueries({
            queryKey: [
              ...queryKeyPrefix,
              "list"
            ]
          });
          qc.invalidateQueries({
            queryKey: [
              ...queryKeyPrefix,
              "detail",
              id
            ]
          });
        }, "onSuccess")
      });
    }, "useUpdate"),
    useRemove: /* @__PURE__ */ __name(() => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: /* @__PURE__ */ __name((id) => client.remove(id), "mutationFn"),
        onSuccess: /* @__PURE__ */ __name((_, id) => {
          qc.invalidateQueries({
            queryKey: [
              ...queryKeyPrefix,
              "list"
            ]
          });
          qc.invalidateQueries({
            queryKey: [
              ...queryKeyPrefix,
              "detail",
              id
            ]
          });
        }, "onSuccess")
      });
    }, "useRemove")
  };
}
__name(createResourceHooks, "createResourceHooks");

// src/query-keys.ts
var queryKeys = {
  root: [
    "kraphene"
  ],
  auth: {
    session: [
      "kraphene",
      "auth",
      "session"
    ],
    sessions: [
      "kraphene",
      "auth",
      "sessions"
    ],
    devices: [
      "kraphene",
      "auth",
      "devices"
    ]
  },
  members: [
    "kraphene",
    "members"
  ],
  chitfunds: [
    "kraphene",
    "chitfunds"
  ]
};

// src/auth-resource.ts
function createAuthResource(fetcherConfig) {
  const fetcher = createFetcher(fetcherConfig);
  return {
    login: /* @__PURE__ */ __name((payload) => fetcher.post("/auth/staff/login", {
      ...payload,
      deviceId: payload.deviceId ?? "web"
    }), "login"),
    exchangeMfa: /* @__PURE__ */ __name((payload) => fetcher.post("/auth/staff/login/mfa", payload), "exchangeMfa"),
    refresh: /* @__PURE__ */ __name((refreshToken) => fetcher.post("/auth/refresh", {
      refreshToken
    }), "refresh"),
    logout: /* @__PURE__ */ __name((refreshToken) => fetcher.post("/auth/logout", {
      refreshToken
    }), "logout"),
    initiatePasswordReset: /* @__PURE__ */ __name((payload) => fetcher.post("/auth/staff/password/reset/initiate", payload), "initiatePasswordReset"),
    confirmPasswordReset: /* @__PURE__ */ __name((payload) => fetcher.post("/auth/staff/password/reset/confirm", payload), "confirmPasswordReset"),
    forceChange: /* @__PURE__ */ __name((payload) => fetcher.post("/auth/password/force-change", payload), "forceChange"),
    sessions: /* @__PURE__ */ __name(() => fetcher.get("/auth/sessions"), "sessions"),
    revokeSession: /* @__PURE__ */ __name((sessionId) => fetcher.delete(`/auth/sessions/${sessionId}`), "revokeSession")
  };
}
__name(createAuthResource, "createAuthResource");

// src/error-translator.ts
var STATUS_TO_CATEGORY = {
  401: "unauthorized",
  403: "forbidden",
  404: "not-found",
  409: "conflict",
  422: "validation",
  429: "rate-limited"
};
function toAppError(error) {
  if (error instanceof ApiError) {
    const category = STATUS_TO_CATEGORY[error.status] ?? (error.status >= 500 ? "server" : "unknown");
    const forwardMessage = category === "validation" || category === "conflict" || error.status === 400;
    return {
      category,
      message: forwardMessage ? error.message : void 0
    };
  }
  if (error instanceof Error) {
    return {
      category: "unknown",
      message: error.message
    };
  }
  return {
    category: "unknown"
  };
}
__name(toAppError, "toAppError");

// src/field-errors.ts
function isZodFlattenedShape(details) {
  return typeof details === "object" && details !== null && ("fieldErrors" in details || "formErrors" in details);
}
__name(isZodFlattenedShape, "isZodFlattenedShape");
function fieldErrorsFromApiError(error) {
  const details = error.details;
  if (isZodFlattenedShape(details)) {
    const fieldErrors = {};
    for (const [key, messages] of Object.entries(details.fieldErrors ?? {})) {
      if (Array.isArray(messages) && messages.length > 0) {
        fieldErrors[key] = String(messages[0]);
      }
    }
    const formErrors = Array.isArray(details.formErrors) ? details.formErrors.map(String) : [];
    return {
      fieldErrors,
      formErrors
    };
  }
  return {
    fieldErrors: {},
    formErrors: [
      error.message
    ]
  };
}
__name(fieldErrorsFromApiError, "fieldErrorsFromApiError");
export {
  ApiError,
  TWO_FACTOR_STEP_UP_REQUIRED,
  createAuthResource,
  createFetcher,
  createResourceClient,
  createResourceHooks,
  fieldErrorsFromApiError,
  normalizeVersionedPath,
  queryKeys,
  toAppError
};
//# sourceMappingURL=index.js.map